import React from "react"

import { useNavigate } from "@tanstack/react-router"
import { XIcon } from "lucide-react"

import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress"
import { BusinessNameStep } from "@/components/onboarding/steps/BusinessNameStep"
import { BusinessStep } from "@/components/onboarding/steps/BusinessStep"
import type { BusinessAnswers } from "@/components/onboarding/steps/BusinessStep"
import { FundingStep } from "@/components/onboarding/steps/FundingStep"
import type { FundingAnswers } from "@/components/onboarding/steps/FundingStep"
import { MatchStep } from "@/components/onboarding/steps/MatchStep"
import { WelcomeStep } from "@/components/onboarding/steps/WelcomeStep"
import { WorkingStep } from "@/components/onboarding/steps/WorkingStep"
import { useOnboardingWizard } from "@/components/onboarding/useOnboardingWizard"
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import type { PrequalifyingFundingNeeds } from "@boundless/bl-api-client/customer/models/PrequalifyingFundingNeeds"

type StepId =
  | "welcome"
  | "business-name"
  | "business"
  | "funding"
  | "working"
  | "match"

/**
 * Steps with no way out — no close button, no Escape. The Company is created
 * from the name, so leaving before that leaves an account with no business for
 * the rest of the product to attach to. It stays a property of the flow rather
 * than of the step because it governs the dialog, which the shell owns.
 */
const LOCKED_STEPS = new Set<StepId>(["welcome", "business-name"])

/**
 * The steps that show dots, in the order the borrower meets them. The wait and
 * the reveal are absent: one is transient and the other is the end of the
 * flow, so a position indicator there would be noise.
 */
const DOTTED_STEPS: StepId[] = [
  "welcome",
  "business-name",
  "business",
  "funding",
]

/** What an earlier, abandoned run of the wizard left behind. */
interface Resume {
  company: { uuid: string; legalName: string } | null
  business: BusinessAnswers | null
  funding: FundingAnswers | null
  matchCount: number | null
}

/**
 * The funding row is the funding step's answers exactly, but for the count the
 * endpoint computed from them, which the reveal is handed on its own.
 */
function fundingAnswers({
  capitalAmount,
  useOfFunds,
  whenFundsNeeded,
}: PrequalifyingFundingNeeds): FundingAnswers {
  return { capitalAmount, useOfFunds, whenFundsNeeded }
}

/**
 * Where a returning borrower picks up: the first question they have not
 * answered yet. Anyone who got as far as naming their business has seen the
 * welcome, so a reload drops them back into the flow rather than at the top.
 */
function resumeStep({ company, business }: Resume): StepId {
  if (!company) {
    return "welcome"
  }
  return business ? "funding" : "business"
}

/**
 * Decides whether the borrower is owed the onboarding wizard, and hands the
 * flow the answers to start from.
 *
 * The decision is latched rather than watched: `shouldOpen` goes false partway
 * through — the name step creates the company, which refetches the queries it
 * is read from, and saving the answers settles it false for good — so watching
 * it would close the wizard mid-flow. Mounting the flow once, from the answers
 * as they stood, also keeps it shut once the borrower is done.
 */
export function OnboardingWizard() {
  const { shouldOpen, resumeCompany, resumeBusiness, resumeFunding } =
    useOnboardingWizard()

  // Written during render on purpose: it captures a first value rather than
  // reacting to a change, so there is nothing here for an effect to do.
  const resume = React.useRef<Resume | null>(null)
  if (shouldOpen && !resume.current) {
    resume.current = {
      // Someone resuming an abandoned wizard keeps the company they already
      // have, so the name step renames it instead of adding a second one.
      company: resumeCompany
        ? { uuid: resumeCompany.uuid, legalName: resumeCompany.legalName }
        : null,
      // The business row is the business step's answers exactly.
      business: resumeBusiness,
      funding: resumeFunding && fundingAnswers(resumeFunding),
      matchCount: resumeFunding?.lenderMatchCount ?? null,
    }
  }

  if (!resume.current) {
    return null
  }

  return <OnboardingFlow resume={resume.current} />
}

/**
 * The guided onboarding wizard shown to a borrower right after signup.
 *
 * The shell: it decides which step is showing and how the dialog behaves
 * around it. Each step owns its own hero, copy, form and the one row it
 * writes; what a step needs from the ones before it is held here and handed
 * down, so going back and forward again finds the answers still in place.
 * Colours derive from `--primary`, which `ThemeWrapper` sets per partner.
 */
function OnboardingFlow({ resume }: { resume: Resume }) {
  const navigate = useNavigate()

  const [open, setOpen] = React.useState(true)
  const [stepId, setStepId] = React.useState<StepId>(() => resumeStep(resume))
  const [direction, setDirection] = React.useState<"forward" | "back">(
    "forward",
  )

  // The company this run of the wizard owns: the one an earlier abandoned run
  // left behind, or the one the name step is about to create.
  const [company, setCompany] = React.useState(resume.company)
  const [business, setBusiness] = React.useState(resume.business)
  const [funding, setFunding] = React.useState(resume.funding)
  const [matchCount, setMatchCount] = React.useState(resume.matchCount)

  // What was typed into a step the borrower walked back out of before it could
  // be saved. Kept apart from the answers above, which are only ever complete
  // rows the server has accepted — a draft may be half-filled, and the reveal
  // must not be able to read one. Each step clears its own on the way forward,
  // so a stale draft can never shadow a newer saved answer.
  const [nameDraft, setNameDraft] = React.useState<string | null>(null)
  const [businessDraft, setBusinessDraft] =
    React.useState<Partial<BusinessAnswers> | null>(null)
  const [fundingDraft, setFundingDraft] =
    React.useState<Partial<FundingAnswers> | null>(null)

  const goTo = (next: StepId, towards: "forward" | "back" = "forward") => {
    setDirection(towards)
    setStepId(next)
  }

  const leave = () => {
    setOpen(false)
    navigate({ to: "/company/details" })
  }

  const finish = React.useCallback(() => {
    setDirection("forward")
    setStepId("match")
  }, [])

  const isLocked = LOCKED_STEPS.has(stepId)
  const dotIndex = DOTTED_STEPS.indexOf(stepId)

  const step = () => {
    switch (stepId) {
      case "welcome":
        return <WelcomeStep onNext={() => goTo("business-name")} />

      case "business-name":
        return (
          <BusinessNameStep
            // A name typed but never saved outlives the trip back to the
            // welcome; once saved, the company carries it instead.
            defaultName={nameDraft ?? company?.legalName ?? ""}
            companyUuid={company?.uuid ?? null}
            onSaved={(saved) => {
              setNameDraft(null)
              setCompany(saved)
              goTo("business")
            }}
            onBack={(draft) => {
              setNameDraft(draft)
              goTo("welcome", "back")
            }}
          />
        )

      case "business":
        return (
          company && (
            <BusinessStep
              companyUuid={company.uuid}
              answers={businessDraft ?? business}
              onNext={(answers) => {
                setBusinessDraft(null)
                setBusiness(answers)
                goTo("funding")
              }}
              onBack={(draft) => {
                setBusinessDraft(draft)
                goTo("business-name", "back")
              }}
            />
          )
        )

      case "funding":
        return (
          company && (
            <FundingStep
              companyUuid={company.uuid}
              answers={fundingDraft ?? funding}
              onNext={(answers, count) => {
                setFundingDraft(null)
                setFunding(answers)
                setMatchCount(count)
                goTo("working")
              }}
              onBack={(draft) => {
                setFundingDraft(draft)
                goTo("business", "back")
              }}
            />
          )
        )

      // The last two steps cannot be returned to: the answers have been
      // written by then, so going back would either re-run the save or offer
      // to edit something the reveal was already computed from.
      case "working":
        return <WorkingStep onDone={finish} />

      case "match":
        return (
          business &&
          funding && (
            <MatchStep
              matchCount={matchCount}
              business={business}
              funding={funding}
              onNext={leave}
            />
          )
        )
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isLocked) {
          return
        }
        setOpen(next)
      }}
    >
      <DialogContent
        allowClosing={false}
        onEscapeKeyDown={(event) => {
          if (isLocked) {
            event.preventDefault()
          }
        }}
        // Leaving is deliberate: the close button, or Escape, but never a
        // stray click on the backdrop.
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="onboarding-wizard"
        data-locked={isLocked ? "true" : undefined}
        aria-describedby={undefined}
        overlayClassName="bg-slate-900/45 backdrop-blur-[14px] backdrop-saturate-150 motion-reduce:backdrop-blur-none"
        className="gap-0 overflow-hidden rounded-[28px] border-0 p-0 shadow-2xl sm:max-w-[560px]"
      >
        {!isLocked && (
          <DialogClose
            data-testid="onboarding-close"
            className="focus-visible:ring-ring/70 absolute top-4 right-4 z-30 flex size-8 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30 focus-visible:ring-2 focus-visible:outline-hidden"
          >
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}

        <div
          // Keying on the step id remounts, restarting the enter animation.
          key={stepId}
          data-testid={`onboarding-step-${stepId}`}
          className={cn(
            "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200",
            direction === "forward"
              ? "motion-safe:slide-in-from-right-4"
              : "motion-safe:slide-in-from-left-4",
          )}
        >
          {step()}
          {dotIndex >= 0 && (
            <OnboardingProgress
              currentIndex={dotIndex}
              total={DOTTED_STEPS.length}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
